import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormikContext, getIn } from 'formik';
import { Grid } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import Field from 'core/components/form/form.v2';
import { LabelTooltip } from 'core/components/label-tooltip';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { propDetailsCodes } from '../quote-forms/quote-forms.constants';
import { MobileHomeRentersForm } from 'common/components/farm/mobile-home-renters-form';
import InfoIcon from 'core/assets/svg/ic-info-dark.svg?react';
import useStyles from './initial-risk-detail.styles';

// Form options constants
const ownershipStatusOptions = lookupsJson.farm.ownershipStatus;
const tohOptions = lookupsJson.typeOfHome;
const hqOptions = lookupsJson.homeQuality;
const ctOptions = lookupsJson.farm.dwellingConstructionType;
const btOptions = lookupsJson.basementType;
const ewtOptions = lookupsJson.exteriorWallType;
const gtOptions = lookupsJson.garageType;
const rtOptions = lookupsJson.farm.dwellingRoofType;
const rsOptions = lookupsJson.roofShape;
const roOptions = lookupsJson.roofOriginal;
const heatingSourceOptions = lookupsJson.farm.primaryHeatingSourceOptions;
const electricalServiceOptions = lookupsJson.farm.electricalService;
const electricalTypesOptions = lookupsJson.farm.electricalType;
const heatingTypeOptions = lookupsJson.farm.heatingType;
// FO-4 (Contents Only) should only show "Contents" option for Residence Type/Usage
const ruOptions = lookupsJson.farm.residenceUsage.filter((option) => option.id === 'C');
const propertyTypeOptions = lookupsJson.farm.propertyType.map((option) => ({
  ...option,
  value: option.value === 'Home' ? 'Home or Rental Unit' : option.value
}));

const nfidOptions = [
  { id: '1', value: '1' },
  { id: '2', value: '2' },
  { id: '3', value: '3' },
  { id: '4', value: '4' }
];

const FO4RiskDetail = () => {
  const classes = useStyles();
  const {
    quote: { getAlerts }
  } = useStore();

  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const alerts = getAlerts(propDetailsCodes, values.policyType);
  const needMoreData = alerts.some((alert) => [5004, 50006].includes(alert.code));
  const initialRiskDetailErrors = useRef(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  // Extract frequently accessed nested values to improve readability
  const farmLocation = values.farmLocations?.[0];
  const farmRisk = farmLocation?.risks?.[0];
  const farmLocationPath = 'farmLocations[0]';
  const farmRiskPath = `${farmLocationPath}.risks[0]`;
  const buildingPath = `${farmRiskPath}.building`;

  const toggleAdditionalFields = useCallback(() => {
    setShowAdditionalFields(!showAdditionalFields);
  }, [showAdditionalFields]);

  useEffect(() => {
    if (alerts.length && initialRiskDetailErrors.current && isSubmitting) {
      initialRiskDetailErrors.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [alerts, isSubmitting]);

  useEffect(() => {
    if (needMoreData) {
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
    }
  }, [needMoreData]);

  // Check if structure category is Mobile Home
  const isMobileHome = farmRisk?.building?.propertyType === 'MH';
  const areasUnderConstruction = getIn(values, `${buildingPath}.areasUnderConstruction`);
  return (
    <Grid container spacing={2}>
      <Field
        xs={6}
        type="select"
        id="farmRisk.building.propertyType"
        name={`${buildingPath}.propertyType`}
        mode="light"
        label="Structure Category*"
        options={propertyTypeOptions}
      />
      <Field
        xs={6}
        type="select"
        id="farmRisk.building.residenceUsage"
        name={`${buildingPath}.residenceUsage`}
        mode="light"
        label="Residence Type/Usage*"
        options={ruOptions}
      />
      <Field
        xs={6}
        type="select"
        id="farmRisk.building.constructionType"
        name={`${buildingPath}.constructionType`}
        mode="light"
        label="Construction Type"
        options={ctOptions}
      />
      <Grid item xs={6}>
        <LabelTooltip
          label="Solid Fuel Burning Appliances*"
          tooltip={{
            label: 'More Info',
            onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
          }}
          mode="light"
        >
          <Field
            type="numeric"
            id="farmRisk.building.numSolidFuelBurningAppliances"
            name={`${buildingPath}.numSolidFuelBurningAppliances`}
            mode="light"
            format={{
              allowNegative: false
            }}
          />
        </LabelTooltip>
      </Grid>

      {isMobileHome && <MobileHomeRentersForm basePath={farmRiskPath} />}

      {/* Risk address automatically inherits from location */}

      <Grid item xs={12} className={classes.rentersInfoTextContainer}>
        <InfoIcon width={16} height={16} />
        <span className={classes.rentersInfoText}>
          Make sure to include a floor, unit, or building number if one exists for this rental.
        </span>
      </Grid>

      {needMoreData && (
        <Grid item xs={12} className={classes.checkboxContainer}>
          <Label type="action" onClick={toggleAdditionalFields}>
            {showAdditionalFields ? 'Hide additional fields' : 'Show additional fields'}
          </Label>
        </Grid>
      )}

      {showAdditionalFields && (
        <>
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.yearBuilt"
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year Built"
          />
          <Field
            xs={4}
            type="select"
            options={tohOptions}
            id="farmRisk.building.typeOfHome"
            name={`${buildingPath}.typeOfHome`}
            mode="light"
            label="Type of home"
          />
          <Field
            name={`${buildingPath}.ownershipStatus`}
            type="select"
            label="Ownership"
            mode="light"
            options={ownershipStatusOptions}
            xs={4}
          />
          <Field
            xs={3}
            type="select"
            options={nfidOptions}
            id="farmRisk.building.numFamiliesInDwelling"
            name={`${buildingPath}.numFamiliesInDwelling`}
            mode="light"
            label="Families living"
          />
          <Field
            xs={3}
            type="select"
            options={hqOptions}
            id="farmRisk.building.homeQuality"
            name={`${buildingPath}.homeQuality`}
            mode="light"
            label="Home quality"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.sqFt"
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Above-ground sqft"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.constructionType"
            options={ctOptions}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Ext. wall construction"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numStories"
            name={`${buildingPath}.numStories`}
            mode="light"
            label="Stories"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numHalfBathrooms"
            name={`${buildingPath}.numHalfBathrooms`}
            mode="light"
            label="Half Bathrooms"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numFullBathrooms"
            name={`${buildingPath}.numFullBathrooms`}
            mode="light"
            label="Full Bathrooms"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numFireplaces"
            name={`${buildingPath}.numFireplaces`}
            mode="light"
            label="Fireplaces"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.numWoodBurningStoves"
            name={`${buildingPath}.numWoodBurningStoves`}
            mode="light"
            label="Wood Burning Stoves"
          />
          <Field
            xs={4}
            type="select"
            id="farmRisk.building.basementType"
            options={btOptions}
            name={`${buildingPath}.basementType`}
            mode="light"
            label="Basement/Foundation type"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.basementSqFt"
            name={`${buildingPath}.basementSqFt`}
            mode="light"
            label="Basement finished sqft"
          />
          <Field
            xs={6}
            type="select"
            id="farmRisk.building.exteriorWallType"
            options={ewtOptions}
            name={`${buildingPath}.exteriorWallType`}
            mode="light"
            label="Exterior wall finish"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.garageType"
            options={gtOptions}
            name={`${buildingPath}.garageType`}
            mode="light"
            label="Type of Garage"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.garageCarCapacity"
            name={`${buildingPath}.garageCarCapacity`}
            mode="light"
            label="Garage's Car space"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.primaryHeatingSource"
            options={heatingSourceOptions}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary Heating"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.secondaryHeatingSource"
            options={heatingSourceOptions}
            name={`${buildingPath}.secondaryHeatingSource`}
            mode="light"
            label="Secondary Heating"
          />
          <Field
            xs={6}
            type="checkbox"
            id="farmRisk.building.areasUnderConstruction"
            name={`${buildingPath}.areasUnderConstruction`}
            mode="light"
            label="Renovations"
          />

          {areasUnderConstruction && (
            <>
              <Field
                xs={4}
                id={`${buildingPath}.renovation.electricalType`}
                name={`${buildingPath}.renovation.electricalType`}
                type="select"
                options={electricalTypesOptions}
                mode="light"
                label="Electric Type"
              />
              <Field
                xs={4}
                id={`${buildingPath}.renovation.electricalService`}
                name={`${buildingPath}.renovation.electricalService`}
                type="select"
                options={electricalServiceOptions}
                mode="light"
                label="Electrical Service Update"
              />
              <Field
                xs={4}
                id={`${buildingPath}.renovation.heatingType`}
                name={`${buildingPath}.renovation.heatingType`}
                type="select"
                options={heatingTypeOptions}
                mode="light"
                label="Heating Type"
              />
              <Field
                xs={4}
                type="numeric"
                id={`${buildingPath}.renovation.heatingYear`}
                name={`${buildingPath}.renovation.heatingYear`}
                mode="light"
                label="Heating Year"
              />
              <Field
                xs={4}
                type="numeric"
                id={`${buildingPath}.renovation.coolingYear`}
                name={`${buildingPath}.renovation.coolingYear`}
                mode="light"
                label="AC Year"
              />
              <Field
                xs={4}
                type="numeric"
                id={`${buildingPath}.renovation.plumbingYear`}
                name={`${buildingPath}.renovation.plumbingYear`}
                mode="light"
                label="Plumbing Year"
              />
              <Field
                xs={4}
                type="numeric"
                id={`${buildingPath}.renovation.electricalYear`}
                name={`${buildingPath}.renovation.electricalYear`}
                mode="light"
                label="Electrical Year"
              />
            </>
          )}

          <Field
            type="select"
            id="farmRisk.building.roofOriginal"
            name={`${buildingPath}.roofOriginal`}
            options={roOptions}
            mode="light"
            label="Original Roof?"
            xs={3}
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.roofYear"
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof Year"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.roofType"
            options={rtOptions}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.roofShape"
            options={rsOptions}
            name={`${buildingPath}.roofShape`}
            mode="light"
            label="Roof shape"
          />
          <Field
            xs={4}
            type="select"
            id="farmRisk.building.constructionType"
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Primary construction type"
            options={ctOptions}
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.latitude"
            name={`${buildingPath}.latitude`}
            mode="light"
            label="Latitude"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.longitude"
            name={`${buildingPath}.longitude`}
            mode="light"
            label="Longitude"
          />
          <Field
            xs={12}
            type="checkbox"
            id="farmRisk.building.swimmingPool"
            name={`${buildingPath}.swimmingPool`}
            mode="light"
            label="Is there an in-ground swimming pool on this dwelling risk?"
          />
        </>
      )}
    </Grid>
  );
};

export default FO4RiskDetail;
